import Box from '@amzn/meridian/box';
import React, { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Thumbnail from '@amzn/meridian/thumbnail';
import { GraphQLTrainingCatalog } from '../../../api/Training/schema';
import thumbnailImage from '../../../Assets/fallback_training.png';
import moment from 'moment';
import { getLanguageName } from './helper';
import AssignmentModal from '../Assignment/AssignmentModal';
import { ExtendedUser } from '../../../Types';

interface CatalogProps {
  catalog: GraphQLTrainingCatalog;
  assignees: ExtendedUser[];
}

const Catalog: React.FC<CatalogProps> = ({ catalog, assignees }) => {
  const intl = useIntl();

  const [openModal, setOpenModal] = useState(false);
  const onClickButton = () => setOpenModal(true);

  const {
    learnTrainingId,
    media,
    multilingualAttributes,
    auditTime,
    metrics,
    duration,
    trainingLanguage,
  } = catalog;

  // Early return if no multilingual attributes
  if (!multilingualAttributes?.[0] || !trainingLanguage) {
    return null;
  }

  // Only one attribute would be marked as default language
  // Based on our record, it always be the one associated with training language
  const defaultLingualAttribute = multilingualAttributes.find(
    attr => attr.isDefaultLanguage && attr.trainingTitle
  );

  if (!defaultLingualAttribute) return null;

  return (
    <Box type='outline' spacingInset='400' minHeight={'120px'}>
      <Row widths={['10%', '90%']} alignmentVertical='center'>
        <Thumbnail
          source={media?.thumbnailImage ?? thumbnailImage}
          size='large'
          type='square'
          data-testid={`catalog-thumbnail-${learnTrainingId}`}
        />
        <Row
          alignmentHorizontal='justify'
          alignmentVertical='top'
          widths={['60%']}
        >
          <Column spacingInset='none none none 400' spacing='none none 300'>
            <Text type='h100'>{defaultLingualAttribute.trainingTitle}</Text>
            <Row>
              <Text type='b200'>{getLanguageName(intl, trainingLanguage)}</Text>
              <Text color='secondary' type='b200'>
                {auditTime ? (
                  <FormattedMessage
                    id='cs_perfportal_training_catalog_last_update_date'
                    defaultMessage='Last updated on {date}'
                    values={{
                      date: moment(auditTime).format('MMMM D, YYYY'),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_not_applicable'
                    defaultMessage='N/A'
                  />
                )}
              </Text>
            </Row>
          </Column>
          <Column spacingInset='none none none 400' spacing='none none 300'>
            <Row spacing='none 200'>
              <Text color='secondary' type='b200'>
                <FormattedMessage
                  id='cs_perfportal_training_catalog_ccx_attribute_header'
                  defaultMessage='CCX:'
                />
              </Text>
              {/* @TODO: Update this with translation mapping once we get the sample data */}
              <Text type='h50'>{metrics}</Text>
            </Row>
            <Row>
              <Text color='secondary' type='b200'>
                {duration ? (
                  <FormattedMessage
                    id='cs_perfportal_training_catalog_duration_min'
                    defaultMessage='Duration: {num} min'
                    values={{
                      num: moment.duration(duration).asMinutes(),
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id='cs_perfportal_not_applicable'
                    defaultMessage='N/A'
                  />
                )}
              </Text>
            </Row>
          </Column>
          <Column>
            <Button size='small' onClick={onClickButton}>
              <FormattedMessage
                id='cs_perfportal_training_catalog_assign_to'
                defaultMessage='Assign to'
              />
            </Button>
            <AssignmentModal
              openModal={openModal}
              setOpenModal={setOpenModal}
              assignees={assignees}
              catalog={catalog}
            />
          </Column>
        </Row>
      </Row>
    </Box>
  );
};

export default Catalog;
